import { LoanType, PaymentOption } from '@/api/LeaBank/leaBank.interfaces';

export interface Applicant {
    nationalId: string;
    birthDate: string;
    firstName: string;
    lastName: string;
    maritalStatus: MaritalStatus;
    hasActiveCollectionCase: boolean;
    hasPaymentRemark: boolean;
    mobileNumber: string;
    emailAddress: string;
    isPEP: boolean;
    numberOfChildrenBelow18: number;
    educationLevel: EducationLevel;
    preferredLanguage: string;
    psd2Data: string;
    numberOfCarsInHousehold?: number;
    dwelling: Dwelling;
    employment: Employment;
    expenses: Expense[];
    income: Income[];
    citizenships: Citizenship[];
    debts: Debt[];
}

// Commented types below are not needed at the moment but existing in BE
// Specific order asked by Leabank.
export enum MaritalStatus {
    Married = 'Married',
    CoHabiting = 'CoHabiting',
    Single = 'Single',
    // Divorced = 'Divorced',
    // Widower = 'Widower',
    // Separated = 'Separated',
}

export enum EducationLevel {
    Bachelor = 'Bachelor',
    PrimarySchool = 'PrimarySchool',
    WorkEducation = 'WorkEducation',
    Master = 'Master',
    HighSchool = 'HighSchool',
}

interface Dwelling {
    type: DwellingType;
    startDate?: string;
}

// Commented types below are not needed at the moment but existing in BE
export enum DwellingType {
    OwnHouse = 'OwnHouse',
    OwnApartment = 'OwnApartment',
    RentalApartment = 'RentalApartment',
    LivingWithParents = 'LivingWithParents',
    Other = 'Other',
    //HousingCooperative = 'HousingCooperative',
    //CompanyHousing = 'CompanyHousing',
}

export interface Employment {
    type: EmploymentType;
    percentage?: number;
    employer?: string;
    position?: string;
    level?: EmploymentLevel;
    startDate?: string;
}

// Comented types below are not needed at the moment but existing in BE
// Specific order asked by Leabank.
export enum EmploymentType {
    PermanentEmployment = 'PermanentEmployment',
    FixedTermWorkContract = 'FixedTermWorkContract',
    Entrepreneur = 'Entrepreneur',
    Other = 'Other',
    Pensioner = 'Pensioner',
    Student = 'Student',
    Unemployed = 'Unemployed',
    //Furlough = 'Furlough',
    //Incapacitated = 'Incapacitated',
    //WorkAssessmentAllowance = 'WorkAssessmentAllowance',
    //StayAtHome = 'StayAtHome',
}

enum EmploymentLevel {
    Employee = 'Employee',
    PublicServant = 'PublicServant',
    IntermediateManager = 'IntermediateManager',
    Manager = 'Manager',
}

export interface Expense {
    amount: number;
    type: ExpenseType;
    period: Period;
}

export enum Period {
    monthly = 'monthly',
    quarterly = 'quarterly',
    yearly = 'yearly',
}

export enum ExpenseType {
    RentalCost = 'RentalCost',
    Alimony = 'Alimony',
    CarLeasing = 'CarLeasing',
    HousingExpenses = 'HousingExpenses',
    OtherExpenses = 'OtherExpenses',
}

export interface Income {
    amount: number;
    type: IncomeType;
    period: Period;
}

export enum IncomeType {
    GrossIncome = 'GrossIncome',
    NetIncome = 'NetIncome',
    PartnerGrossIncome = 'PartnerGrossIncome',
    RentalIncome = 'RentalIncome',
    InsuranceIncome = 'InsuranceIncome',
    AlimonyIncome = 'AlimonyIncome',
    GrossHouseholdIncome = 'GrossHouseholdIncome',
    OtherIncome = 'OtherIncome',
}

interface Citizenship {
    countryCode: string;
}

export interface Debt {
    debtType?: DebtType;
    bankName?: string;
    remainingPaymentMonths?: number;
    outstandingAmount?: number;
    monthlyPaymentAmount?: number;
    redeem?: boolean;
    interestRate?: number;
    creditLimit?: number;
    bankAccountNumber?: string;
    cid?: string;
    percentageResponsible?: number;
}

export enum DebtType {
    ConsumerLoan = 'ConsumerLoan',
    Creditcard = 'Creditcard',
    MortgageLoan = 'MortgageLoan',
    StudentLoan = 'StudentLoan',
    VehicleLoan = 'VehicleLoan',
    FlexibleMortgage = 'FlexibleMortgage',
    FlexibleConsumerLoan = 'FlexibleConsumerLoan',
    ChargeCard = 'ChargeCard',
    HousingCooperativeSharedDebt = 'HousingCooperativeSharedDebt',
    Other = 'Other',
}

export type ApplicantForm = BasicApplicantForm | MediumApplicantForm | HighApplicantForm;

export interface BasicApplicantForm {
    type: LoanType;
    plan: PaymentOption | undefined;
    nationalId: string | undefined;
    monthlyGrossIncome: number | undefined;
    dwellingType: DwellingType | undefined;
    numberOfChildrenBelow18: number | undefined;
    employmentType: EmploymentType | undefined;
    maritalStatus: MaritalStatus | undefined;
    terms: boolean | undefined;
    monthlyRentalCostExpense: number | undefined;
}

export interface BasicApplicant {
    nationalId: string;
    numberOfChildrenBelow18: number;
    dwelling: Dwelling;
    employment: Employment;
    maritalStatus: MaritalStatus;
    income: Income[];
    expenses: Expense[];
}

export interface MediumApplicantForm extends BasicApplicantForm {
    monthlyPaymentAmountDebt: number | undefined;
}

export interface MediumApplicant extends BasicApplicant {
    debts: Debt[];
}

export interface HighApplicantForm extends MediumApplicantForm {
    hasPaymentRemark: boolean | undefined;
    educationLevel: EducationLevel | undefined;
    mortageOutstandingAmountDebt: number | undefined;
    monthlyPaymentAmountDebt: number | undefined;
    monthlyCarLeasingExpense: number | undefined;
    employer: string | undefined;
    employmentStartDate: string | undefined;
}

export interface HighApplicant extends MediumApplicant {
    hasPaymentRemark: boolean;
    maritalStatus: MaritalStatus;
    educationLevel: EducationLevel;
}
